import React, { useMemo, useRef, useState } from "react";

import TourInformation from "../components/TourInformation";
import TourCanvas from "../components/TourCanvas";
import ModeSelect from "../components/ModeSelect";
import StudioLayout from "../components/studio/StudioLayout";
import StudioToolbar from "../components/studio/StudioToolbar";
import StudioInspector from "../components/studio/StudioInspector";
import ScenesPanel from "../components/studio/ScenesPanel";
import EditorAlerts from "../components/studio/EditorAlerts";
import OffscreenIndicators from "../components/studio/OffscreenIndicators";
// import PanArrows from "../components/studio/PanArrows";
import { TOOLS } from "../components/studio/ToolToggle";
import { buildFloorIndex } from "../components/studio/imageFilter";
import { tidyBoard } from "../components/studio/tidyBoard";

const EMPTY_FILTER = { status: [], floor: null, tags: [] };

const NodeEditor = ({
  onUpdateImage,
  onUpdateBoomerang,
  onUpdateActiveImage,
  onAddImage,
  onDeleteImage,
  onSaveTour,
  onEmbedTour,
  onExit,
  onUpdateTour,
  onSaveCurrentInfo,
  currentInfo,
  activeImage,
  onTourSettings,
  tour,
  dirty,
}) => {
  // Shared studio state lives here so the panels and the canvas agree on what's
  // visible. The FiltersPanel (in the inspector) writes it; TourCanvas and the
  // minimap read it through the shared `matchesStudioFilter` helper.
  const [filter, setFilter] = useState(EMPTY_FILTER);

  // Canvas tool (WP-L): 'pan' drags the board, 'select' draws a marquee.
  const [tool, setTool] = useState(TOOLS.PAN);

  // Ids selected by the marquee. Kept separate from `activeImage` (which still
  // drives the inspector/preview); the toolbar's "Fit Selected" frames this set
  // when it is non-empty.
  const [selectedIds, setSelectedIds] = useState([]);

  // imageId → floor uuid, derived from the floor plans' hotspots, so the floor
  // dropdown can hide/show nodes without storing a floor on every image.
  const floorIndex = useMemo(
    () => buildFloorIndex(tour.data.floorplans),
    [tour.data.floorplans],
  );

  // The Canvas API exposed by TourCanvas on mount. Held here so panels mounted
  // in the StudioLayout slots (WP-A…K) can drive the camera through it.
  const canvasApiRef = useRef(null);

  // Pre-tidy positions (id → { x, y }), kept so Tidy Board can be reverted.
  // Cleared once the user undoes; replaced on the next tidy.
  const [tidyUndoSnapshot, setTidyUndoSnapshot] = useState(null);

  // Tidy Board (WP-A wiring of WP-I): grid-pack every node into a clean layout
  // and persist the new coordinates per node, then frame the result.
  const handleTidyBoard = () => {
    const positions = tidyBoard(tour.data.images, {
      startImageId: tour.data.startImageId,
    });

    setTidyUndoSnapshot(
      Object.fromEntries(
        Object.entries(tour.data.images).map(([id, image]) => [
          id,
          { x: image.x, y: image.y },
        ]),
      ),
    );

    // Apply every node's new position in a single tour update. Calling
    // onUpdateImage in a loop doesn't work: it rebuilds the tour from the same
    // stale snapshot each time, so the batched setState keeps only the last
    // node's move.
    const nextImages = { ...tour.data.images };
    Object.entries(positions).forEach(([id, { x, y }]) => {
      const image = nextImages[id];
      if (image) nextImages[id] = { ...image, x, y };
    });

    onUpdateTour({
      ...tour,
      data: { ...tour.data, images: nextImages },
    });

    // Frame the result on the next frame, once TourCanvas has re-rendered with
    // the new positions (fitToView reads the canvas' image props).
    requestAnimationFrame(() => {
      const api = canvasApiRef.current;
      if (api) api.fitToView({ all: true });
    });
  };

  // Restore every node to where it was before the last Tidy Board. Nodes added
  // since the tidy keep their current position.
  const handleUndoTidyBoard = () => {
    if (!tidyUndoSnapshot) return;

    const nextImages = { ...tour.data.images };
    Object.entries(tidyUndoSnapshot).forEach(([id, { x, y }]) => {
      const image = nextImages[id];
      if (image) nextImages[id] = { ...image, x, y };
    });

    onUpdateTour({
      ...tour,
      data: { ...tour.data, images: nextImages },
    });
    setTidyUndoSnapshot(null);

    requestAnimationFrame(() => {
      const api = canvasApiRef.current;
      if (api) api.fitToView({ all: true });
    });
  };

  return (
    <StudioLayout
      toolbar={
        <StudioToolbar
          canvasApiRef={canvasApiRef}
          activeImage={activeImage}
          selectedIds={selectedIds}
          images={tour.data.images}
          onSelectImage={onUpdateActiveImage}
          onTidyBoard={handleTidyBoard}
          onUndoTidyBoard={tidyUndoSnapshot ? handleUndoTidyBoard : null}
          onTourSettings={onTourSettings}
          onEmbedTour={onEmbedTour}
          onSaveTour={onSaveTour}
          onExit={onExit}
          dirty={dirty}
        />
      }
      left={
        <ScenesPanel
          onAddImage={onAddImage}
          images={tour.data.images}
          tourInfo={{ account: tour.account, team: tour.team }}
        />
      }
      center={
        <>
          <TourCanvas
            images={tour.data.images}
            onUpdateImage={onUpdateImage}
            onUpdateBoomerang={onUpdateBoomerang}
            onUpdateActiveImage={onUpdateActiveImage}
            onDeleteImage={onDeleteImage}
            activeImage={activeImage}
            tour={tour}
            onUpdateTour={onUpdateTour}
            onSaveTour={onSaveTour}
            onEmbedTour={onEmbedTour}
            onSaveCurrentInfo={onSaveCurrentInfo}
            currentInfo={currentInfo}
            filter={filter}
            floorIndex={floorIndex}
            tool={tool}
            selectedIds={selectedIds}
            onSelectImages={setSelectedIds}
            onReady={api => {
              canvasApiRef.current = api;
            }}
          />
          <OffscreenIndicators
            canvasApiRef={canvasApiRef}
            images={tour.data.images}
            activeImage={activeImage}
          />
          {/* <PanArrows canvasApiRef={canvasApiRef} /> */}
          <TourInformation
            title={tour.title}
            token={tour.token}
            updatedAt={tour.updated_at ? tour.updated_at : null}
            onUpdateTitle={title => {
              const newTour = Object.assign({}, tour, {
                title,
              });

              onUpdateTour(newTour);
            }}
          />
        </>
      }
      right={
        <StudioInspector
          canvasApiRef={canvasApiRef}
          images={Object.values(tour.data.images)}
          activeImage={activeImage}
          filter={filter}
          setFilter={setFilter}
          floorplans={tour.data.floorplans ?? []}
          floorIndex={floorIndex}
        />
      }
      statusBar={
        <>
          <EditorAlerts
            canvasApiRef={canvasApiRef}
            images={tour.data.images}
            onSelectImage={onUpdateActiveImage}
            tool={tool}
            onChangeTool={setTool}
          />
          <ModeSelect />
        </>
      }
    />
  );
};

export default NodeEditor;
